/**
 * Tasks: types, pure Y.Doc operations, limits, the achievement catalog and
 * REST shapes. Lives in shared for the same reason doc.ts does: the browser,
 * the agent and the sync server all write task docs and must produce
 * identically shaped entries.
 *
 * A task list is a tree item (type "tasklist") plus its own Y.Doc named
 * task.<spaceId>.<listId>, holding one Y.Map under DOC_KEYS.taskItems:
 * taskId -> Y.Map of plain fields. Per-field LWW in a map-per-task means a
 * concurrent done-toggle and title edit merge instead of clobbering, the
 * same invariant as tree items.
 */
import * as Y from "yjs";
import { DOC_KEYS } from "./model.js";
import { newId } from "./ids.js";
import { nextDueDate, parseRecurrence, serializeRecurrence } from "./recurrence.js";
import { compareSortKeys, sortKeyBetween } from "./sort.js";
export const MAX_TASK_TITLE = 300;
export const MAX_TASK_DESCRIPTION = 10_000;
export function taskItems(doc) {
    return doc.getMap(DOC_KEYS.taskItems);
}
export function readTaskFrom(id, m) {
    return {
        id,
        title: m.get("title") ?? "",
        done: m.get("done") ?? false,
        doneAt: m.get("doneAt"),
        doneBy: m.get("doneBy"),
        description: m.get("description"),
        dueDate: m.get("dueDate"),
        dueTime: m.get("dueTime"),
        sortKey: m.get("sortKey") ?? "a0",
        createdAt: m.get("createdAt") ?? 0,
        createdBy: m.get("createdBy") ?? "",
        seq: m.get("seq"),
        // through the validator so a malformed value degrades to "not recurring"
        // instead of throwing inside a projection
        recurrence: parseRecurrence(m.get("recurrence")) ?? undefined,
        respawnedTo: m.get("respawnedTo"),
        spawnedFrom: m.get("spawnedFrom"),
        sourceId: m.get("sourceId"),
    };
}
/** Every task in the list, in sort-key order. */
export function readTasks(doc) {
    const out = [];
    taskItems(doc).forEach((m, id) => out.push(readTaskFrom(id, m)));
    out.sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
    return out;
}
export function getTask(doc, id) {
    const m = taskItems(doc).get(id);
    return m ? readTaskFrom(id, m) : undefined;
}
/**
 * The one place a task's field shape is written. Callers pass the sortKey
 * because the cheap way to place one task (read the list) and the cheap way
 * to place a thousand (walk a running key) differ.
 */
function buildTaskMap(fields, createdBy, sortKey, now) {
    const m = new Y.Map();
    m.set("title", fields.title.slice(0, MAX_TASK_TITLE));
    m.set("done", fields.done === true);
    if (fields.done === true) {
        m.set("doneAt", fields.doneAt ?? now);
        if (fields.doneBy)
            m.set("doneBy", fields.doneBy);
    }
    if (fields.description)
        m.set("description", fields.description.slice(0, MAX_TASK_DESCRIPTION));
    if (fields.dueDate)
        m.set("dueDate", fields.dueDate);
    if (fields.dueTime)
        m.set("dueTime", fields.dueTime);
    if (fields.recurrence)
        m.set("recurrence", { ...fields.recurrence });
    if (fields.spawnedFrom)
        m.set("spawnedFrom", fields.spawnedFrom);
    if (fields.sourceId)
        m.set("sourceId", fields.sourceId);
    m.set("sortKey", sortKey);
    m.set("createdAt", now);
    m.set("createdBy", createdBy);
    return m;
}
/**
 * Create a task with a caller-minted id (invariant 3), placed at the TOP of the
 * list: what you just typed is what you are thinking about, so it belongs where
 * you are looking rather than below everything already there.
 *
 * The key goes before the smallest key in the doc, done tasks included, so a
 * freshly generated key can never collide with one sitting in the done pile.
 */
export function createTaskIn(doc, id, fields, createdBy, origin) {
    const all = readTasks(doc);
    const firstKey = all[0]?.sortKey ?? null;
    const now = Date.now();
    doc.transact(() => {
        taskItems(doc).set(id, buildTaskMap(fields, createdBy, sortKeyBetween(null, firstKey), now));
    }, origin);
}
/** Mutate one task's fields. No-op if it is gone. */
export function updateTask(doc, id, fn, origin) {
    const m = taskItems(doc).get(id);
    if (!m)
        return;
    doc.transact(() => fn(m), origin);
}
export function setTaskDone(doc, id, done, userId, origin) {
    updateTask(doc, id, (m) => {
        m.set("done", done);
        if (done) {
            m.set("doneAt", Date.now());
            m.set("doneBy", userId);
        }
        else {
            m.delete("doneAt");
            m.delete("doneBy");
        }
    }, origin);
}
/** Reorder among UNDONE siblings; beforeId semantics match moveItem. */
export function moveTask(doc, id, beforeId, origin) {
    const siblings = readTasks(doc).filter((t) => !t.done && t.id !== id);
    let prev;
    let next;
    if (beforeId === undefined) {
        prev = siblings[siblings.length - 1];
    }
    else if (beforeId === null) {
        next = siblings[0];
    }
    else {
        const idx = siblings.findIndex((s) => s.id === beforeId);
        next = siblings[idx] ?? undefined;
        prev = idx > 0 ? siblings[idx - 1] : undefined;
    }
    updateTask(doc, id, (m) => m.set("sortKey", sortKeyBetween(prev?.sortKey ?? null, next?.sortKey ?? null)), origin);
}
/**
 * Complete-to-next-instance respawn for recurring tasks. Returns the new
 * task's id, or null when nothing was done. Preconditions are re-checked
 * INSIDE the transaction, so a projection replay or two racing callers can
 * only ever produce one spawn: the guard is respawnedTo on the doc, not any
 * DB row. createdBy is copied from the original, not the completer: a
 * recurring task's instances belong to whoever set it up.
 */
export function respawnRecurringTask(doc, doneTaskId, newTaskId, today, origin) {
    let spawned = null;
    doc.transact(() => {
        const m = taskItems(doc).get(doneTaskId);
        if (!m)
            return;
        const task = readTaskFrom(doneTaskId, m);
        if (!task.done || !task.recurrence || task.respawnedTo !== undefined)
            return;
        m.set("respawnedTo", newTaskId);
        createTaskIn(doc, newTaskId, {
            title: task.title,
            description: task.description,
            dueDate: nextDueDate(task.recurrence, task.dueDate, today),
            dueTime: task.dueTime,
            recurrence: task.recurrence,
            spawnedFrom: doneTaskId,
        }, task.createdBy, origin);
        spawned = newTaskId;
    }, origin);
    return spawned;
}
/** Permanent removal. The projection keeps a tombstone row for stats. */
export function deleteTask(doc, id, origin) {
    const items = taskItems(doc);
    if (!items.has(id))
        return;
    doc.transact(() => items.delete(id), origin);
}
/**
 * Apply a batch of imported tasks in one transaction, matching on sourceId so
 * running the same import twice updates instead of duplicating.
 *
 * Rules, deliberately one-way (the source of truth is the other system, and
 * an import must never destroy local work):
 *  - fields that differ are rewritten; identical entries are left completely
 *    untouched, because a no-op write would still wake the sync store, the
 *    projection and the Discord reconciler for nothing
 *  - not done here but done at the source moves to done, keeping the source's
 *    completion time
 *  - done here is NEVER reopened, whatever the source says
 *  - tasks that vanished at the source are left alone; the caller reports the
 *    count rather than deleting anything
 *
 * Placement walks a running sortKey from the current maximum, so a thousand
 * inserts cost a thousand key computations instead of a thousand list scans.
 */
export function applyTaskImport(doc, entries, createdBy, origin) {
    const outcome = { created: 0, updated: 0, skipped: 0 };
    if (entries.length === 0)
        return outcome;
    const items = taskItems(doc);
    const existing = readTasks(doc);
    const bySource = new Map();
    for (const t of existing)
        if (t.sourceId)
            bySource.set(t.sourceId, t);
    let tail = existing.length > 0 ? existing[existing.length - 1].sortKey : null;
    const now = Date.now();
    doc.transact(() => {
        for (const entry of entries) {
            const prior = bySource.get(entry.sourceId);
            if (!prior) {
                tail = sortKeyBetween(tail, null);
                const id = newId();
                items.set(id, buildTaskMap({ ...entry, sourceId: entry.sourceId }, createdBy, tail, now));
                // so a duplicated sourceId inside one batch updates rather than
                // inserting twice
                bySource.set(entry.sourceId, { ...entry, id, sortKey: tail, createdAt: now, createdBy, done: entry.done === true });
                outcome.created++;
                continue;
            }
            const m = items.get(prior.id);
            if (!m)
                continue;
            let touched = false;
            const set = (key, value) => {
                m.set(key, value);
                touched = true;
            };
            const clear = (key) => {
                m.delete(key);
                touched = true;
            };
            const title = entry.title.slice(0, MAX_TASK_TITLE);
            if (title !== prior.title)
                set("title", title);
            const description = entry.description ? entry.description.slice(0, MAX_TASK_DESCRIPTION) : undefined;
            if (description !== (prior.description ?? undefined)) {
                if (description)
                    set("description", description);
                else if (prior.description !== undefined)
                    clear("description");
            }
            if (entry.dueDate !== prior.dueDate) {
                if (entry.dueDate)
                    set("dueDate", entry.dueDate);
                else if (prior.dueDate !== undefined)
                    clear("dueDate");
            }
            if (entry.dueTime !== prior.dueTime) {
                if (entry.dueTime && entry.dueDate)
                    set("dueTime", entry.dueTime);
                else if (prior.dueTime !== undefined)
                    clear("dueTime");
            }
            const wanted = entry.recurrence ? serializeRecurrence(entry.recurrence) : null;
            const held = prior.recurrence ? serializeRecurrence(prior.recurrence) : null;
            if (wanted !== held) {
                if (entry.recurrence)
                    set("recurrence", { ...entry.recurrence });
                else
                    clear("recurrence");
            }
            if (entry.done === true && !prior.done) {
                set("done", true);
                set("doneAt", entry.doneAt ?? now);
            }
            if (touched)
                outcome.updated++;
            else
                outcome.skipped++;
        }
    }, origin);
    return outcome;
}
// ---------- default list resolution ----------
/**
 * The canonical default list among a space's live items: the builtin-flagged
 * tasklist with the smallest createdAt (tiebreak smallest id). Every reader
 * (web, agent, sync's task-mutate) resolves by this rule so behavior is
 * deterministic even before a heal pass has cleared duplicate flags.
 */
export function resolveDefaultList(items) {
    const candidates = items.filter((i) => i.type === "tasklist" && i.builtin === "tasks" && !i.trashedAt);
    candidates.sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
    return candidates[0];
}
export const ACHIEVEMENTS = [
    { key: "completed_1", kind: "completed", threshold: 1, title: "Getting started", description: "Complete your first task." },
    { key: "completed_10", kind: "completed", threshold: 10, title: "Ten down", description: "Complete 10 tasks." },
    { key: "completed_50", kind: "completed", threshold: 50, title: "Half century", description: "Complete 50 tasks." },
    { key: "completed_100", kind: "completed", threshold: 100, title: "Century", description: "Complete 100 tasks." },
    { key: "completed_250", kind: "completed", threshold: 250, title: "Task machine", description: "Complete 250 tasks." },
    { key: "completed_500", kind: "completed", threshold: 500, title: "Five hundred strong", description: "Complete 500 tasks." },
    { key: "completed_1000", kind: "completed", threshold: 1000, title: "Thousand club", description: "Complete 1,000 tasks." },
    { key: "streak_3", kind: "streak", threshold: 3, title: "Warming up", description: "Complete tasks 3 days in a row." },
    { key: "streak_7", kind: "streak", threshold: 7, title: "Week streak", description: "Complete tasks 7 days in a row." },
    { key: "streak_14", kind: "streak", threshold: 14, title: "Fortnight", description: "Complete tasks 14 days in a row." },
    { key: "streak_30", kind: "streak", threshold: 30, title: "Monthly devotion", description: "Complete tasks 30 days in a row." },
    { key: "streak_100", kind: "streak", threshold: 100, title: "Hundred days", description: "Complete tasks 100 days in a row." },
    { key: "day_10", kind: "day", threshold: 10, title: "Power day", description: "Complete 10 tasks in one day." },
    { key: "day_25", kind: "day", threshold: 25, title: "Unstoppable", description: "Complete 25 tasks in one day." },
    { key: "created_100", kind: "created", threshold: 100, title: "Idea factory", description: "Create 100 tasks." },
];
//# sourceMappingURL=tasks.js.map